/**
 * One shared way to read an order form as a list of legs.
 *
 * Under the hood every order is "one or more legs". Single-leg forms keep
 * their assets on the order-level fields (which mirror the single row in
 * `public.legs`), and multi-leg forms keep them either on the order level
 * (shared mode) or on each leg (per-leg mode). Every rule that asks a
 * per-stop question — tractor presence, trailer/trailer type, trip type,
 * eligibility, out-of-service — should read this list instead of branching
 * on `order_type` itself.
 */
import type { OrderFormData, LegData } from "@/components/orders/orderFormTypes";
import { isPerLegAssets, isPerLegTripType } from "@/lib/order-form-assets";

export interface ResolvedFormLeg {
  /** Zero-based position; `Stop ${index + 1}` in user-facing copy. */
  index: number;
  /** Row id in `public.legs`, when the leg already exists. */
  id?: string;
  tractorId: string;
  operatorId: string;
  operator2Id: string;
  trailerId: string;
  trailerTypeId: string;
  tripType: string;
  readyTime: string;
  /** Ready-window end, defaulting to the start when unset. */
  readyTimeEnd: string;
  deliveryTime: string;
  /** Delivery-window end, defaulting to the start when unset. */
  deliveryTimeEnd: string;
  /** Window ends exactly as entered — blank when the user hasn't set one.
   *  Presence rules must read these, not the defaulted values above. */
  readyTimeEndRaw: string;
  deliveryTimeEndRaw: string;
  shipFromStreetAddress: string;
  shipFromCity: string;
  shipToStreetAddress: string;
  shipToCity: string;
  /** The underlying leg row, when the form holds one. */
  leg?: LegData;
}

const s = (v: unknown): string => (typeof v === "string" ? v : "");

/**
 * Flattens the form into its legs. A single-leg order yields a list of one,
 * built from the order-level fields. A multi-leg order in shared-asset mode
 * yields each leg with the order-level assets applied.
 */
export function resolveFormLegs(fd: OrderFormData): ResolvedFormLeg[] {
  const legs = fd.legs ?? [];
  if (fd.order_type !== "multi-leg" || legs.length === 0) {
    return [
      {
        index: 0,
        tractorId: s(fd.assigned_tractor_id),
        operatorId: s(fd.assigned_operator_id),
        operator2Id: s(fd.assigned_operator_2_id),
        trailerId: s(fd.trailer_id),
        trailerTypeId: s(fd.trailer_type_id),
        tripType: s(fd.trip_type),
        readyTime: s(fd.ready_time),
        readyTimeEnd: s(fd.ready_time_end) || s(fd.ready_time),
        deliveryTime: s(fd.requested_delivery_time),
        deliveryTimeEnd:
          s(fd.requested_delivery_time_end) || s(fd.requested_delivery_time),
        readyTimeEndRaw: s(fd.ready_time_end),
        deliveryTimeEndRaw: s(fd.requested_delivery_time_end),
        shipFromStreetAddress: s(fd.ship_from_street_address),
        shipFromCity: s(fd.ship_from_city),
        shipToStreetAddress: s(fd.ship_to_street_address),
        shipToCity: s(fd.ship_to_city),
      },
    ];
  }

  const perLegAssets = isPerLegAssets(fd);
  const perLegTripType = isPerLegTripType(fd);

  return legs.map((leg, index) => ({
    index,
    id: leg.id,
    tractorId: perLegAssets ? s(leg.assigned_tractor_id) : s(fd.assigned_tractor_id),
    operatorId: perLegAssets ? s(leg.assigned_operator_id) : s(fd.assigned_operator_id),
    operator2Id: perLegAssets
      ? s(leg.assigned_operator_2_id)
      : s(fd.assigned_operator_2_id),
    trailerId: perLegAssets ? s(leg.trailer_id) : s(fd.trailer_id),
    trailerTypeId: perLegAssets ? s(leg.trailer_type_id) : s(fd.trailer_type_id),
    tripType: perLegTripType ? s(leg.trip_type) : s(fd.trip_type),
    readyTime: s(leg.ready_time),
    readyTimeEnd: s(leg.ready_time_end) || s(leg.ready_time),
    deliveryTime: s(leg.requested_delivery_time),
    deliveryTimeEnd:
      s(leg.requested_delivery_time_end) || s(leg.requested_delivery_time),
    readyTimeEndRaw: s(leg.ready_time_end),
    deliveryTimeEndRaw: s(leg.requested_delivery_time_end),
    shipFromStreetAddress: s(leg.ship_from_street_address),
    shipFromCity: s(leg.ship_from_city),
    shipToStreetAddress: s(leg.ship_to_street_address),
    shipToCity: s(leg.ship_to_city),
    leg,
  }));
}
